import { isNumberString } from "@corca/core";
import { sortBy } from "es-toolkit";

import { getSuggestionsDoc } from "@/components/suggestions/suggestionsDoc";
import { isSuggestionOfType } from "@/components/suggestions/utils";
import { createNodeFrom } from "@/models/nodes/createNodeFrom";
import { isUnitChild } from "@/models/nodes/utils";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { isLabelOrSubscript } from "@/models/utils/semanticChecks";
import { hasOneCodepoint } from "@/utils/codePointLength";
import { containsSpecialCharacter } from "@/utils/containsSpecialCharacter";

import { createSuggestionFromNode } from "./utils";
import { filterSuggestions, prioritizeSuggestions } from "./utils/filterSuggestions";
import { symbolLookupFor } from "./utils/symbolLookup";

import type { SymbolLookup } from "./utils/symbolLookup";
import type { SingleSuggestion, SuggestionProvider } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { GroupKeyFilter } from "@/models/NodeLookupModel";
import type { NodeModel } from "@/models/nodes/types";
import type { SuggestionLabelJson, LocaleKeywords, NodeJson } from "@corca/data";

type ValueNodeType = "variable" | "constant" | "function";

const suggestionsBySymbolKeyCache = new WeakMap<
  SingleSuggestion<ValueNodeType>[],
  Record<string, SingleSuggestion<ValueNodeType>>
>();

function suggestionsBySymbolKey<T extends ValueNodeType>(
  suggestions: SingleSuggestion<T>[],
): Record<string, SingleSuggestion<T>> {
  let bySymbolKey = suggestionsBySymbolKeyCache.get(suggestions);
  if (!bySymbolKey) {
    bySymbolKey = {};
    for (const suggestion of suggestions) {
      const symbolKey = suggestion.item.symbolProps?.key;
      if (symbolKey) {
        bySymbolKey[symbolKey] = suggestion;
      }
    }
    suggestionsBySymbolKeyCache.set(suggestions, bySymbolKey);
  }
  return bySymbolKey as Record<string, SingleSuggestion<T>>;
}

export type Options = {
  modifyInput?: (str: string) => string;
  skipNewNode?: boolean;
  skipSymbol?: boolean;
  filterExistingNodes?: GroupKeyFilter;
  /**
   * Which symbols a new node may be named after when the typed name matches one, such as `alpha`
   * for α; the constant provider allows the Greek alphabet only.
   */
  symbolFilter?: (suggestion: SingleSuggestion) => boolean;
};

/**
 * `externalSuggestions` gives the known items to offer in a document; the JSON providers return
 * fewer while that document serves their file from the libraries.
 */
export function valueNodeSuggestionsProvider<T extends ValueNodeType>(
  type: T,
  externalSuggestions: (doc: DocumentModel) => SingleSuggestion<T>[] = () => [],
  providerOptions: Options = {},
): SuggestionProvider<T, T> {
  return {
    type,
    search: (str, doc, lang, searchOptions) => {
      const { modifyInput, skipNewNode, skipSymbol, filterExistingNodes, symbolFilter } = {
        ...providerOptions,
        ...searchOptions,
      };

      if (doc.selection.hasTextSelection) {
        return [];
      }

      // Functions, constants, and variables are not valid unit content; never suggest them
      // inside a unit bracket.
      if (isUnitChild(doc.cursor.node)) {
        return [];
      }

      const suggestionsDoc = getSuggestionsDoc();
      const tokens = str.split("_");
      const name = tokens[0] ?? "";

      if (!name || tokens.length > 2) {
        return [];
      }
      const subscript = tokens[1] ?? "";

      const input = modifyInput ? modifyInput(name) : name;
      const lcInput = input.toLowerCase();

      const options: { key: string; suggestion: SingleSuggestion<T> }[] = [];

      const nodes: Map<string, NodeModel<T>> = new Map();
      const existingKeys: Set<string> = new Set();
      let symbols = new Set<string>();

      if (doc.nodeLookup) {
        const existingNodeGroups = doc.nodeLookup.groupByKey({
          filter: filterExistingNodes ?? ((_, t) => t === type),
        });

        for (const [key, { ids }] of existingNodeGroups.entries()) {
          const nodeId = ids.values().next().value;
          const node = nodeId ? (doc.nodes.get(nodeId) as NodeModel<T>) : undefined;
          if (node) {
            nodes.set(key, node);
          }
        }

        symbols = new Set([...existingNodeGroups.values()].map(({ base }) => base));
      }

      const docSuggestions = externalSuggestions(doc);
      const externalOptions = filterSuggestions(str, docSuggestions, {
        filterFn: (suggestion) => {
          if (suggestion.context && !suggestion.context.includes("single")) return false;

          if (isSuggestionOfType(suggestion, "function") && suggestion.item.props?.isBuiltIn) {
            return true;
          }

          if (suggestion.item.isSymbol) {
            return true; // suggestion.item.symbol !== name;
          }

          return !symbols.has(suggestion.item.symbol);
        },
        lang,
        nodeLookup: doc.nodeLookup,
        skipPrioritize: true,
      });

      const externalOptionsExcludingSymbol = externalOptions.filter((sugg) => {
        return !sugg.item.isSymbol || sugg.item.symbol !== name;
      });

      const symbolLookup = symbolLookupFor(doc);

      const oneLetter = hasOneCodepoint(input);

      // Push existing nodes as suggests to suggestion box
      for (const [key, node] of nodes) {
        if (node.value && !("isBuiltIn" in node && node.isBuiltIn) && !existingKeys.has(key)) {
          const valueMatch = checkInputMatchedByValue(
            input,
            node.value,
            false,
            oneLetter,
            symbolLookup,
          );
          let nameMatch = false;
          let priority = 1.6;

          if (oneLetter) {
            nameMatch = node.name.normalize("NFKC").toLowerCase() === lcInput;
          } else {
            nameMatch = node.name.normalize("NFKC").toLowerCase().startsWith(lcInput);
          }

          let keywordsMatch = false;
          const symbolKey = node.symbolNode?.props?.key;

          if (oneLetter && !valueMatch && symbolKey) {
            const keywords = (suggestionsBySymbolKey(docSuggestions)[symbolKey]?.item.keywords ??
              symbolLookup.byKey(symbolKey)?.keywords)?.[lang];

            if (keywords?.[lcInput]) {
              keywordsMatch = true;
              priority += keywords[lcInput];
            }
          }

          if (valueMatch || nameMatch || keywordsMatch) {
            const labelStr = node.symbolNode?.nonEmptyAttachments
              .map((l) => {
                return l.textContent;
              })
              .join("");
            const subscriptMatch = subscript ? labelStr?.startsWith(subscript) : true;

            if (subscriptMatch) {
              if (labelStr && isLabelOrSubscript(doc.cursor.node?.parent)) {
                priority = 0;
              }

              if (node.is(VariableNodeModel) && node.hasParams && !node.power) {
                suggestionsDoc.transact(
                  () => {
                    const noParamsNode = createNodeFrom(node, suggestionsDoc);
                    for (const param of noParamsNode.parameters) {
                      suggestionsDoc.removeNode(param);
                    }

                    const noParamsKey = doc.nodeLookup?.buildKey(noParamsNode, {
                      uniqBy: "labels",
                    });

                    if (noParamsKey && !existingKeys.has(noParamsKey)) {
                      existingKeys.add(noParamsKey);
                      options.push({
                        key,
                        suggestion: {
                          ...createSuggestionFromNode(noParamsNode, { priority: priority + 0.1 }),
                          popupGroup: "existing",
                        },
                      });
                    }
                  },
                  "createNoParamsNodeForSuggestion",
                  { runInAction: true },
                );
              }

              existingKeys.add(key);

              options.push({
                key,
                suggestion: {
                  ...createSuggestionFromNode(node, { priority }),
                  popupGroup: "existing",
                },
              });
            }
          }
        }
      }

      // Push new suggest based on provided type to suggestion box
      if (!skipNewNode) {
        const priority = doc.input.editingNode ? 0 : 1.5;
        const typedGlyph = symbolLookup.byGlyph(input);
        let newNode = getNewNodeSuggestion({
          type,
          doc,
          symbol: input,
          symbolProps: typedGlyph?.key ? { key: typedGlyph.key } : undefined,
          keywords: typedGlyph?.keywords,
          existingKeys,
          priority,
          subscript,
        });
        if (newNode) {
          options.push(newNode);
        } else {
          const firstSymbol = symbolLookup.bestMatch(input, {
            lang,
            nodeLookup: doc.nodeLookup,
            filter: symbolFilter,
          });

          if (firstSymbol) {
            newNode = getNewNodeSuggestion({
              type,
              doc,
              symbol: firstSymbol.item.symbol,
              symbolProps: firstSymbol.item.key ? { key: firstSymbol.item.key } : undefined,
              existingKeys,
              keywords: firstSymbol.item.keywords,
              priority: firstSymbol.priority + 0.5,
              subscript,
            });
            if (newNode) {
              options.push(newNode);
            }
          }
        }
      }

      const sortedOptions = sortBy(options, [({ key }) => key]);

      let suggestions = sortedOptions.map(({ suggestion }) => suggestion);

      if (skipSymbol) {
        suggestions.push(...externalOptionsExcludingSymbol.filter((item) => !item.item.isSymbol));
      } else {
        suggestions.push(...externalOptionsExcludingSymbol);
      }

      suggestions = prioritizeSuggestions(str, suggestions, doc.nodeLookup, lang);

      return [suggestions];
    },
  };
}

// [\u24D0-\u24E9] - small circled symbols
// [\u24B6-\u24CF] - capital circled symbols
// [\u2460-\u24FF] - enclosed alphanumerics
// [\u2776-\u277F] - circled number
const SYMBOL_REGEX =
  /^\p{Letter}|\p{Currency_Symbol}|[\u24B6-\u24CF]|[\u24D0-\u24E9]|[\u2460-\u24FF]|[\u2776-\u277F]$/u;

function createValueNodeJson<T extends "variable" | "constant" | "function">(
  type: T,
  symbol: string,
  subscript?: string,
): NodeJson {
  return {
    type,
    props: {},
    children: [
      {
        type: "symbol",
        props: {
          value: symbol,
        },
        children: subscript
          ? [
              {
                type: "label",
                props: {
                  position: "right-bottom",
                },
                children: [
                  {
                    type: "plainText",
                    props: {
                      value: subscript,
                    },
                    children: [],
                  },
                ],
              },
            ]
          : [],
      },
    ],
  };
}

// TODO: use suggestionsDoc.create
function getNewNodeSuggestion<T extends "variable" | "constant" | "function">({
  type,
  doc,
  symbol,
  symbolProps,
  keywords,
  existingKeys,
  priority = 1,
  subscript,
}: {
  type: T;
  doc: DocumentModel;
  symbol: string;
  symbolProps?: { key: string };
  existingKeys: Set<string>;
  keywords?: LocaleKeywords;
  priority?: number;
  subscript: string;
}) {
  const inputKey = doc.nodeLookup?.buildKey(createValueNodeJson(type, symbol, subscript)) || "";
  const isInEditingNode = !!doc.input.editingNode;
  const { from, to } = doc.editor.tiptap?.state.selection ?? {};
  const isRangeSelection = !doc.cursor.isActive && from !== to;
  const isUpperCase = /^[A-Z]{2,5}$/.test(symbol);

  // allow letters and symbols (e.g. math or currency symbols)
  const isOneLetterOrSymbol = hasOneCodepoint(symbol) && SYMBOL_REGEX.test(symbol);

  // Push new suggest based on provided type to suggestion box
  if (
    (isOneLetterOrSymbol || isInEditingNode || isRangeSelection || isUpperCase) &&
    doc.input.value.length < 20 &&
    /^\S*$/.test(doc.input.value) &&
    !existingKeys.has(inputKey) &&
    !isNumberString(symbol) &&
    !containsSpecialCharacter(symbol)
  ) {
    const labels: SuggestionLabelJson[] | undefined = subscript
      ? [
          {
            position: "right-bottom",
            nodes: [
              isNumberString(subscript)
                ? {
                    type: "number",
                    value: subscript,
                  }
                : {
                    type: "plainText",
                    value: subscript,
                  },
            ],
          },
        ]
      : undefined;

    return {
      key: inputKey,
      suggestion: {
        type: "single",
        item: {
          type,
          intlName: type,
          category: "New",
          symbol,
          symbolProps,
          isNewNode: true,
          labels,
          keywords,
        },
        priority,
        popupGroup: "new",
      } as SingleSuggestion<T>,
    };
  }
  return undefined;
}

function checkInputMatchedByValue(
  input: string,
  value: string,
  caseSensitive: boolean,
  oneLetter: boolean,
  symbols: SymbolLookup,
) {
  const lcInput = input.normalize("NFKC").toLowerCase();
  const lcValue = value.normalize("NFKC").toLowerCase();
  let matched = caseSensitive ? value.startsWith(input) : lcValue.startsWith(lcInput);

  // ignore matching existing variables by one letter keywords, as it can be confusing for the user
  if (!matched && !oneLetter) {
    if (caseSensitive) {
      matched = symbols.keywordsOf(value).some((kw) => kw.startsWith(input));
    } else {
      matched = [...symbols.keywordsOf(value), ...symbols.keywordsOf(lcValue)].some((kw) =>
        kw.toLowerCase().startsWith(lcInput),
      );
    }
  }

  return matched;
}
